import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import sseService from '../utils/sseService';
import { notificationEvents } from '../utils/notificationEvents';
import { sanitizeNotifications } from '../utils/sanitization';
import { BASE_URL } from '../config';

const NotificationContext = createContext();

export const useNotifications = () => {
	const context = useContext(NotificationContext);
	if (!context) {
		throw new Error('useNotifications must be used within a NotificationProvider');
	}
	return context;
};

export const NotificationProvider = ({ children }) => {
	const [notificationCount, setNotificationCount] = useState(0);
	const [recentNotifications, setRecentNotifications] = useState([]);
	const [recentLoading, setRecentLoading] = useState(false);
	const [sseConnected, setSseConnected] = useState(false);
	const [lastFetchTime, setLastFetchTime] = useState(null);
	const [isFetchingCount, setIsFetchingCount] = useState(false);
	const [fetchingRequests, setFetchingRequests] = useState(new Set());
	const [error, setError] = useState(null);
	const [connectionError, setConnectionError] = useState(false);
	
	// Circuit breaker state to prevent infinite retries
	const [circuitBreakerOpen, setCircuitBreakerOpen] = useState(false);
	const [failureCount, setFailureCount] = useState(0);
	const [lastFailureTime, setLastFailureTime] = useState(null);
	const [backoffDelay, setBackoffDelay] = useState(1000);
	
	const token = useSelector((state) => state.admin.token);
	
	// Circuit breaker constants
	const MAX_FAILURES = 3;
	const CIRCUIT_BREAKER_TIMEOUT = 5 * 60 * 1000; // 5 minutes
	const MAX_BACKOFF_DELAY = 5 * 60 * 1000; // 5 minutes

	// Circuit breaker logic
	const shouldAllowRequest = useCallback(() => {
		if (!circuitBreakerOpen) return true;
		
		// Check if circuit breaker timeout has passed
		const now = Date.now();
		if (lastFailureTime && (now - lastFailureTime) > CIRCUIT_BREAKER_TIMEOUT) {
			console.log('🔄 NotificationContext: Circuit breaker timeout passed, attempting to close');
			setCircuitBreakerOpen(false);
			setFailureCount(0);
			setBackoffDelay(1000);
			return true;
		}
		
		return false;
	}, [circuitBreakerOpen, lastFailureTime, CIRCUIT_BREAKER_TIMEOUT]);

	const handleRequestFailure = useCallback(() => {
		const newFailureCount = failureCount + 1;
		setFailureCount(newFailureCount);
		setLastFailureTime(Date.now());
		
		if (newFailureCount >= MAX_FAILURES) {
			console.warn(`🚨 NotificationContext: Circuit breaker OPEN after ${newFailureCount} failures`);
			setCircuitBreakerOpen(true);
			setError('Notification service temporarily unavailable - circuit breaker activated');
		}
		
		// Exponential backoff
		const newDelay = Math.min(backoffDelay * 2, MAX_BACKOFF_DELAY);
		setBackoffDelay(newDelay);
	}, [failureCount, backoffDelay, MAX_FAILURES, MAX_BACKOFF_DELAY]);

	const handleRequestSuccess = useCallback(() => {
		if (failureCount > 0 || circuitBreakerOpen) {
			console.log('✅ NotificationContext: Request succeeded, resetting circuit breaker');
			setFailureCount(0);
			setCircuitBreakerOpen(false);
			setBackoffDelay(1000);
		}
	}, [failureCount, circuitBreakerOpen]);

	// Centralized API calls with circuit breaker protection
	const fetchNotificationCount = useCallback(async () => {
		if (!token || isFetchingCount) return;
		
		// Circuit breaker check
		if (!shouldAllowRequest()) {
			console.log('🚫 NotificationContext: Circuit breaker OPEN, skipping request');
			return;
		}
		
		setIsFetchingCount(true);
		try {
			const response = await fetch(`${BASE_URL}/api/admin/forms/notifications/events/count`, {
				headers: { Authorization: `Bearer ${token}` },
			});

			if (response.ok) {
				const data = await response.json();
				setNotificationCount(data.count);
				setLastFetchTime(new Date());
				setError(null); // Clear any previous errors
				setConnectionError(false);
				handleRequestSuccess();
			} else {
				const errorMsg = `Failed to fetch notification count: ${response.status}`;
				console.error(errorMsg);
				setError(errorMsg);
				if (response.status >= 500) {
					setConnectionError(true);
				}
				handleRequestFailure();
			}
		} catch (error) {
			console.error('Error fetching notification count:', error);
			setError('Network error: Unable to fetch notifications');
			setConnectionError(true);
			handleRequestFailure();
		} finally {
			setIsFetchingCount(false);
		}
	}, [token, isFetchingCount, shouldAllowRequest, handleRequestFailure, handleRequestSuccess]);

	const fetchRecentNotifications = useCallback(async (limit = 5) => {
		if (!token) return;
		
		const requestKey = `recent-${limit}`;
		if (fetchingRequests.has(requestKey)) {
			if (process.env.NODE_ENV === 'development') {
				console.log('🔄 NotificationContext: Request already in progress, skipping');
			}
			return;
		}
		
		setFetchingRequests(prev => new Set(prev).add(requestKey));
		setRecentLoading(true);
		
		try {
			const response = await fetch(
				`${BASE_URL}/api/admin/forms/notifications/recent?limit=${limit}`,
				{
					headers: { Authorization: `Bearer ${token}` },
				}
			);

			if (response.ok) {
				const data = await response.json();
				// Handle different response formats and sanitize content
				const rawNotifications = data.notifications || data.events || data || [];
				const sanitizedNotifications = sanitizeNotifications(rawNotifications);
				setRecentNotifications(sanitizedNotifications);
				setLastFetchTime(new Date());
			}
		} catch (error) {
			console.error('Error fetching recent notifications:', error);
		} finally {
			setRecentLoading(false);
			setFetchingRequests(prev => {
				const newSet = new Set(prev);
				newSet.delete(requestKey);
				return newSet;
			});
		}
	}, [token, fetchingRequests]);

	const markAllAsSeen = useCallback(async () => {
		if (!token) return;
		
		try {
			await fetch(`${BASE_URL}/api/admin/forms/notifications/events/mark-all-seen`, {
				method: 'POST',
				headers: { Authorization: `Bearer ${token}` },
			});

			// Update UI immediately
			setNotificationCount(0);
			setRecentNotifications([]);

			// Notify other components
			notificationEvents.markAllAsSeen();
		} catch (error) {
			console.error('Error marking all notifications as seen:', error);
		}
	}, [token]);

	const markNotificationAsSeen = useCallback(async (notificationId) => {
		if (!token) return;

		try {
			await fetch(
				`${BASE_URL}/api/admin/forms/notifications/events/${notificationId}/mark-seen`,
				{
					method: 'POST',
					headers: { Authorization: `Bearer ${token}` },
				}
			);

			// Update local state immediately
			setNotificationCount((prev) => Math.max(0, prev - 1));
			setRecentNotifications((prev) =>
				prev.filter((notification) => notification.id !== notificationId)
			);

			// Notify other components
			notificationEvents.markAsSeen(notificationId);
		} catch (error) {
			console.error('Error marking notification as seen:', error);
		}
	}, [token]);

	// Initial data fetch when token is available
	useEffect(() => {
		if (!token) return;
		
		if (process.env.NODE_ENV === 'development') {
			console.log('🔄 NotificationContext: Initial data fetch');
		}
		fetchNotificationCount();
	}, [token]);

	// Polling fallback for when SSE is not connected (with circuit breaker protection)
	useEffect(() => {
		if (!token || sseConnected || circuitBreakerOpen) return;

		if (process.env.NODE_ENV === 'development') {
			console.log('🔄 NotificationContext: Starting fallback polling');
		}
		
		// Use much longer polling interval - we only need this as an emergency fallback
		// Since SSE should handle real-time updates, polling can be infrequent
		const FALLBACK_POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes - much less aggressive
		let pollCount = 0;
		
		const poll = () => {
			// Respect circuit breaker
			if (!shouldAllowRequest()) {
				if (process.env.NODE_ENV === 'development') {
					console.log('🚫 NotificationContext: Polling skipped due to circuit breaker');
				}
				return;
			}
			
			pollCount++;
			if (process.env.NODE_ENV === 'development') {
				console.log(`🔄 NotificationContext: Fallback poll #${pollCount} (every 5 minutes)`);
			}
			
			fetchNotificationCount();
		};
		
		const interval = setInterval(poll, FALLBACK_POLL_INTERVAL);
		
		return () => {
			if (process.env.NODE_ENV === 'development') {
				console.log('🔄 NotificationContext: Stopping fallback polling');
			}
			clearInterval(interval);
		};
	}, [token, sseConnected, circuitBreakerOpen, shouldAllowRequest]);

	// SSE connection management
	useEffect(() => {
		if (!token) return;

		// Connect to SSE (async)
		const connectSSE = async () => {
			try {
				await sseService.connect(token);
			} catch (error) {
				console.error('🔔 NotificationContext: Failed to connect SSE:', error);
			}
		};
		
		connectSSE();

		// SSE event handlers
		const handleSSEConnected = () => {
			if (process.env.NODE_ENV === 'development') {
				console.log('🔔 NotificationContext: SSE connected');
			}
			setSseConnected(true);
		};

		const handleSSEDisconnected = () => {
			if (process.env.NODE_ENV === 'development') {
				console.log('🔔 NotificationContext: SSE disconnected');
			}
			setSseConnected(false);
		};

		const handleSSEError = () => {
			if (process.env.NODE_ENV === 'development') {
				console.log('🔔 NotificationContext: SSE error');
			}
			setSseConnected(false);
			setConnectionError(true);
			setError('Real-time updates temporarily unavailable');
		};

		const handleNewNotification = (data) => {
			if (process.env.NODE_ENV === 'development') {
				console.log('🔔 NotificationContext: New notification via SSE:', data);
			}
			// Increment count immediately instead of fetching from API (faster, no delay)
			setNotificationCount((prev) => prev + 1);
		};

		const handleCountUpdate = (data) => {
			if (process.env.NODE_ENV === 'development') {
				console.log('🔔 NotificationContext: Count update via SSE:', data.count);
			}
			setNotificationCount(data.count);
		};

	const handleNotificationSeen = (data) => {
		if (process.env.NODE_ENV === 'development') {
			console.log('🔔 NotificationContext: Notification marked as seen via SSE:', data.notificationId);
		}
		// Don't update count here - let the count_update event handle it
		// This prevents race conditions between the decrement and the database query

		// Remove from recent notifications if present
		setRecentNotifications((prev) =>
			prev.filter((notification) => notification.id !== data.notificationId)
		);
	};

	const handleAllNotificationsSeen = (data) => {
		if (process.env.NODE_ENV === 'development') {
			console.log('🔔 NotificationContext: All notifications marked as seen via SSE');
		}
		// Clear all notifications - trust the optimistic update
		setNotificationCount(0);
		setRecentNotifications([]);
	};

		// Add SSE event listeners
		sseService.addEventListener('connected', handleSSEConnected);
		sseService.addEventListener('disconnected', handleSSEDisconnected);
		sseService.addEventListener('error', handleSSEError);
		sseService.addEventListener('new_notification', handleNewNotification);
		sseService.addEventListener('count_update', handleCountUpdate);
		sseService.addEventListener('notification_seen', handleNotificationSeen);
		sseService.addEventListener('all_notifications_seen', handleAllNotificationsSeen);

		// Custom event listeners for cross-component communication
		const handleMarkAllAsSeenEvent = () => {
			setNotificationCount(0);
			setRecentNotifications([]);
		};

		// Removed handleMarkAsSeenEvent - this was causing double-decrement
		// The SSE handler (handleNotificationSeen) already handles this

		const handleRefreshCountEvent = () => {
			fetchNotificationCount();
		};

		notificationEvents.addEventListener(notificationEvents.MARK_ALL_AS_SEEN, handleMarkAllAsSeenEvent);
		// Removed MARK_AS_SEEN listener - SSE handler is sufficient
		notificationEvents.addEventListener(notificationEvents.REFRESH_COUNT, handleRefreshCountEvent);

		// Cleanup
		return () => {
			// Remove SSE event listeners
			sseService.removeEventListener('connected', handleSSEConnected);
			sseService.removeEventListener('disconnected', handleSSEDisconnected);
			sseService.removeEventListener('error', handleSSEError);
			sseService.removeEventListener('new_notification', handleNewNotification);
			sseService.removeEventListener('count_update', handleCountUpdate);
			sseService.removeEventListener('notification_seen', handleNotificationSeen);
			sseService.removeEventListener('all_notifications_seen', handleAllNotificationsSeen);

			// Disconnect SSE service to prevent memory leaks
			sseService.disconnect();

			// Remove custom event listeners
			notificationEvents.removeEventListener(notificationEvents.MARK_ALL_AS_SEEN, handleMarkAllAsSeenEvent);
			notificationEvents.removeEventListener(notificationEvents.REFRESH_COUNT, handleRefreshCountEvent);
		};
	}, [token]);

	// Memoize context value to prevent unnecessary re-renders
	const value = useMemo(() => ({
		// State
		notificationCount,
		recentNotifications,
		recentLoading,
		sseConnected,
		lastFetchTime,
		error,
		connectionError,

		// Actions (already memoized with useCallback)
		fetchNotificationCount,
		fetchRecentNotifications,
		markAllAsSeen,
		markNotificationAsSeen,

		// Utilities
		refreshData: fetchNotificationCount,
		clearError: () => setError(null),
	}), [
		notificationCount,
		recentNotifications,
		recentLoading,
		sseConnected,
		lastFetchTime,
		error,
		connectionError,
		fetchNotificationCount,
		fetchRecentNotifications,
		markAllAsSeen,
		markNotificationAsSeen
	]);

	return (
		<NotificationContext.Provider value={value}>
			{children}
		</NotificationContext.Provider>
	);
};